import React, { useMemo, useState } from 'react';
import { tendApi, BloomPublishResponse } from '../../api/tendApi';
import LogoSelector from '../brand/LogoSelector';
import { Logo } from '../../api/logoApi';
import SmartPublishButton from './SmartPublishButton';

export interface PlatformPreview {
  id: string;
  name: string;
  icon: React.ReactNode;
  color: string;
}

export interface TendedContentItem {
  platform: PlatformPreview;
  content: string;
  secondaryText?: string;
  headline?: string;
  hashtags: string[];
  mentions: string[];
  mediaUrls: string[];
  primaryImageUrl?: string | null;
  mediaPrompt?: string;
  metadata: Record<string, unknown>;
  optimalPostingTime?: string;
  attributionText?: string;
  engagementHooks?: string[];
  transformationId: number;
  status: string;
  timestamp?: string;
  sourceUrl?: string;
  contentId?: number;
  socialContentId?: number;
  // Video fields (for POLLINATE content)
  videoUrl?: string | null;
  videoThumbnailUrl?: string | null;
  videoDurationSeconds?: number | null;
  pollinateVideoId?: number | null;
  // Brand and logo fields (for title/logo overlay)
  brandId?: number;
  brandName?: string;
  contentTitle?: string;
  brandDefaultLogo?: any; // Logo type from logoApi
  brandLogoDisplayMode?: 'icon_only' | 'name_only' | 'icon_and_name';
  contentItemLogoId?: number | null;
  contentItemLogoDisplayMode?: 'icon_only' | 'name_only' | 'icon_and_name' | null;
  // Curator brand ID for publishing (different from brandId which is the original publisher)
  curatorBrandId?: number;
}

interface TendedPlatformPreviewProps {
  item: TendedContentItem;
}

const formatHashtags = (hashtags: string[]) => {
  return hashtags
    .filter(Boolean)
    .map((tag) => (tag.startsWith('#') ? tag : `#${tag}`));
};

const formatMentions = (mentions: string[]) => {
  return mentions
    .filter(Boolean)
    .map((mention) => (mention.startsWith('@') ? mention : `@${mention}`));
};

const formatUrl = (url?: string) => {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    return {
      display: parsed.hostname.replace('www.', ''),
      href: url,
    };
  } catch {
    return {
      display: url,
      href: url,
    };
  }
};

interface InstagramCarouselProps {
  images: string[];
  contentTitle?: string;
  brandId?: number;
  brandName?: string;
  defaultLogo?: Logo | null;
  logoDisplayMode?: 'icon_only' | 'name_only' | 'icon_and_name';
}

const InstagramCarousel: React.FC<InstagramCarouselProps> = ({
  images,
  contentTitle,
  brandId,
  brandName,
  defaultLogo,
  logoDisplayMode = 'icon_and_name'
}) => {
  const [activeIndex, setActiveIndex] = useState(0);

  if (!images.length) return null;

  const goPrev = () => setActiveIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  const goNext = () => setActiveIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));

  const isFirstImage = activeIndex === 0;
  const isLastImage = activeIndex === images.length - 1;
  const showOverlay = contentTitle && brandId;

  return (
    <div className="relative mb-3" style={{ aspectRatio: '2/3' }}>
      <img
        src={images[activeIndex]}
        alt={`Instagram preview ${activeIndex + 1}`}
        className="w-full h-full object-cover rounded-lg border border-gray-200"
      />

      {/* Title and Logo Overlay on First Image */}
      {showOverlay && isFirstImage && (
        <>
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent rounded-lg pointer-events-none" />
          <div className="absolute bottom-12 left-0 right-0 p-4 text-center z-10">
            <h3 className="text-white text-lg font-semibold mb-2 leading-tight" style={{ textShadow: '0 2px 4px rgba(0, 0, 0, 0.8)' }}>
              {contentTitle}
            </h3>
            <div className="flex justify-center scale-75">
              <LogoSelector
                brandId={brandId}
                brandName={brandName}
                defaultLogo={defaultLogo}
                logoDisplayMode={logoDisplayMode}
                canEdit={false}
                onChange={() => {}}
                skipBrandUpdate={true}
              />
            </div>
          </div>
        </>
      )}

      {/* Logo Only Overlay on Last Image */}
      {showOverlay && isLastImage && images.length > 1 && (
        <>
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent rounded-lg pointer-events-none" />
          <div className="absolute bottom-12 left-0 right-0 p-4 text-center z-10">
            <div className="flex justify-center scale-75">
              <LogoSelector
                brandId={brandId}
                brandName={brandName}
                defaultLogo={defaultLogo}
                logoDisplayMode={logoDisplayMode}
                canEdit={false}
                onChange={() => {}}
                skipBrandUpdate={true}
              />
            </div>
          </div>
        </>
      )}

      {images.length > 1 && (
        <>
          <button
            type="button"
            onClick={goPrev}
            className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/60 text-white rounded-full px-3 py-1 text-sm z-20"
          >
            ◀
          </button>
          <button
            type="button"
            onClick={goNext}
            className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/60 text-white rounded-full px-3 py-1 text-sm z-20"
          >
            ▶
          </button>
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1 z-20">
            {images.map((_, index) => (
              <span
                key={index}
                className={`h-2 w-2 rounded-full ${index === activeIndex ? 'bg-white' : 'bg-white/40'}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const ThreadList: React.FC<{ posts: string[] }> = ({ posts }) => {
  if (!posts.length) return null;

  return (
    <div className="space-y-3">
      {posts.map((post, index) => (
        <div key={index} className="bg-white border border-gray-200 rounded-lg p-3">
          <div className="text-xs text-gray-400 mb-1">{index + 1}/{posts.length}</div>
          <p className="text-sm text-gray-800 whitespace-pre-line leading-relaxed">{post}</p>
        </div>
      ))}
    </div>
  );
};

const LinkPreview: React.FC<{ url?: string }> = ({ url }) => {
  const formatted = formatUrl(url);
  if (!formatted) return null;

  return (
    <a
      href={formatted.href}
      target="_blank"
      rel="noopener noreferrer"
      className="block border border-gray-200 rounded-lg p-3 hover:border-gray-300 transition"
    >
      <div className="text-xs text-gray-500">{formatted.display}</div>
      <div className="text-sm text-blue-600 truncate">{formatted.href}</div>
    </a>
  );
};

const CommonFooter: React.FC<{ item: TendedContentItem }> = ({ item }) => {
  if (!item.optimalPostingTime && !item.attributionText && !(item.engagementHooks && item.engagementHooks.length)) {
    return null;
  }

  return (
    <div className="mt-4 border-t border-gray-100 pt-3 text-xs text-gray-500 space-y-2">
      {item.optimalPostingTime && (
        <div>
          <span className="font-medium text-gray-600">Optimal time:</span> {item.optimalPostingTime}
        </div>
      )}
      {item.engagementHooks && item.engagementHooks.length > 0 && (
        <div>
          <span className="font-medium text-gray-600">Hooks:</span> {item.engagementHooks.join(', ')}
        </div>
      )}
      {item.attributionText && (
        <div className="italic">{item.attributionText}</div>
      )}
    </div>
  );
};

const TendedPlatformPreview: React.FC<TendedPlatformPreviewProps> = ({ item }) => {
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishResult, setPublishResult] = useState<BloomPublishResponse | null>(null);
  const [publishError, setPublishError] = useState<string | null>(null);

  const hashtags = useMemo(() => formatHashtags(item.hashtags || []), [item.hashtags]);
  const mentions = useMemo(() => formatMentions(item.mentions || []), [item.mentions]);

  // Logo resolution logic (same as ContentCard)
  // Note: brandDefaultLogo is actually the RESOLVED logo from the backend
  // The backend already handles the hierarchy: content item logo > brand default logo
  const resolvedLogo = useMemo(() => {
    return item.brandDefaultLogo || null;
  }, [item.brandDefaultLogo]);

  const resolvedDisplayMode = useMemo(() => {
    if (item.contentItemLogoDisplayMode) {
      return item.contentItemLogoDisplayMode;
    }
    return item.brandLogoDisplayMode || 'icon_and_name';
  }, [item.contentItemLogoDisplayMode, item.brandLogoDisplayMode]);

  const images = useMemo(() => {
    let result: string[] = [];
    if (item.mediaUrls && item.mediaUrls.length > 0) {
      result = item.mediaUrls;
    } else if (item.primaryImageUrl) {
      result = [item.primaryImageUrl];
    }

    return result;
  }, [item.mediaUrls, item.primaryImageUrl, item.platform.id]);

  const threadPosts = useMemo(() => {
    const candidate = (item.metadata?.thread ?? []) as unknown;
    const result = Array.isArray(candidate) ? candidate.filter(Boolean).map(String) : [];
    return result;
  }, [item.metadata, item.platform.id]);

  const renderHashtagRow = () => {
    if (hashtags.length === 0) return null;
    return (
      <div className="flex flex-wrap gap-1 mt-3">
        {hashtags.map((tag, index) => (
          <span
            key={index}
            className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full"
          >
            {tag}
          </span>
        ))}
      </div>
    );
  };

  const renderMentions = () => {
    if (mentions.length === 0) return null;
    return (
      <div className="text-xs text-blue-500 mt-2">
        {mentions.join(' ')}
      </div>
    );
  };

  const handlePublishToBluesky = async () => {
    if (!item.socialContentId) {
      setPublishError('Missing social content ID - cannot publish');
      return;
    }

    setIsPublishing(true);
    setPublishError(null);
    setPublishResult(null);

    try {
      const result = await tendApi.publishToBluesky(item.socialContentId);
      setPublishResult(result);
    } catch (error) {
      setPublishError(error instanceof Error ? error.message : 'Failed to publish to Bluesky');
    } finally {
      setIsPublishing(false);
    }
  };

  const renderContentByPlatform = () => {
    switch (item.platform.id) {
      case 'instagram':
        return (
          <>
            {/* Show video player if video_url exists (POLLINATE content) */}
            {item.videoUrl ? (
              <div className="relative mb-3">
                <video
                  src={item.videoUrl}
                  controls
                  poster={item.videoThumbnailUrl || undefined}
                  className="w-full max-h-96 rounded-lg border border-gray-200 bg-black"
                  playsInline
                >
                  Your browser does not support the video tag.
                </video>
                {item.videoDurationSeconds && (
                  <div className="mt-1 text-xs text-gray-500">
                    Duration: {Math.round(item.videoDurationSeconds)}s
                  </div>
                )}
              </div>
            ) : images.length === 1 ? (
              // Single image - show title/logo overlay (Instagram 2:3 aspect ratio)
              <div className="relative mb-3" style={{ aspectRatio: '2/3' }}>
                <img
                  src={images[0]}
                  alt="Instagram preview"
                  className="w-full h-full object-cover rounded-lg border border-gray-200"
                />
                {/* Title and Logo Overlay */}
                {item.contentTitle && item.brandId && (
                  <>
                    {/* Gradient overlay for text readability - dark at bottom fading up */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent rounded-lg pointer-events-none" />
                    {/* Title and logo positioned absolutely at bottom */}
                    <div className="absolute bottom-0 left-0 right-0 p-4 text-center z-10">
                      <h3 className="text-white text-lg font-semibold mb-2 leading-tight" style={{ textShadow: '0 2px 4px rgba(0, 0, 0, 0.8)' }}>
                        {item.contentTitle}
                      </h3>
                      <div className="flex justify-center scale-75">
                        <LogoSelector
                          brandId={item.brandId}
                          brandName={item.brandName}
                          defaultLogo={resolvedLogo}
                          logoDisplayMode={resolvedDisplayMode}
                          canEdit={false}
                          onChange={() => {}}
                          skipBrandUpdate={true}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : images.length > 1 ? (
              // Multiple images - carousel with title/logo on first image, logo only on last
              <InstagramCarousel
                images={images}
                contentTitle={item.contentTitle}
                brandId={item.brandId}
                brandName={item.brandName}
                defaultLogo={resolvedLogo}
                logoDisplayMode={resolvedDisplayMode}
              />
            ) : null}
            <p className="text-sm text-gray-800 whitespace-pre-line leading-relaxed">{item.content}</p>
            {renderHashtagRow()}
            {renderMentions()}
            {item.metadata?.call_to_action && (
              <div className="mt-3 text-sm font-medium text-pink-500">
                {String(item.metadata.call_to_action)}
              </div>
            )}
            <CommonFooter item={item} />
          </>
        );
      case 'twitter':
        return (
          <div className="space-y-3">
            {/* Show video player if video_url exists (POLLINATE content) */}
            {item.videoUrl ? (
              <div className="relative mb-3">
                <video
                  src={item.videoUrl}
                  controls
                  poster={item.videoThumbnailUrl || undefined}
                  className="w-full max-h-96 rounded-lg border border-gray-200 bg-black"
                  playsInline
                >
                  Your browser does not support the video tag.
                </video>
                {item.videoDurationSeconds && (
                  <div className="mt-1 text-xs text-gray-500">
                    Duration: {Math.round(item.videoDurationSeconds)}s
                  </div>
                )}
              </div>
            ) : images.length > 0 && (
              <div className="relative mb-3">
                <img
                  src={images[0]}
                  alt="Twitter preview"
                  className="w-full h-48 object-cover rounded-lg border border-gray-200"
                />
                {/* Title and Logo Overlay */}
                {item.contentTitle && item.brandId && (
                  <>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent rounded-lg pointer-events-none" />
                    <div className="absolute bottom-0 left-0 right-0 p-3 text-center z-10">
                      <h3 className="text-white text-base font-semibold mb-2 leading-tight" style={{ textShadow: '0 2px 4px rgba(0, 0, 0, 0.8)' }}>
                        {item.contentTitle}
                      </h3>
                      <div className="flex justify-center scale-75">
                        <LogoSelector
                          brandId={item.brandId}
                          brandName={item.brandName}
                          defaultLogo={resolvedLogo}
                          logoDisplayMode={resolvedDisplayMode}
                          canEdit={false}
                          onChange={() => {}}
                          skipBrandUpdate={true}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
            {/* Show full thread if available, otherwise show primary text */}
            {threadPosts.length > 0 ? (
              <ThreadList posts={threadPosts} />
            ) : (
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <p className="text-sm text-gray-900 whitespace-pre-line leading-relaxed">{item.content}</p>
                {renderMentions()}
                {renderHashtagRow()}
              </div>
            )}
            <CommonFooter item={item} />
          </div>
        );
      case 'linkedin':
        return (
          <>
            {item.headline && (
              <h4 className="text-base font-semibold text-gray-900 mb-2">{item.headline}</h4>
            )}
            <p className="text-sm text-gray-800 whitespace-pre-line leading-relaxed">{item.content}</p>
            {renderMentions()}
            {renderHashtagRow()}
            {(item.sourceUrl || images.length > 0) && (
              <div className="mt-3 space-y-3">
                {images.length > 0 && (
                  <div className="relative">
                    <img
                      src={images[0]}
                      alt="LinkedIn preview"
                      className="w-full h-48 object-cover rounded-lg border border-gray-200"
                    />
                    {/* Title and Logo Overlay */}
                    {item.contentTitle && item.brandId && (
                      <>
                        {/* Gradient overlay for text readability - dark at bottom fading up */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent rounded-lg pointer-events-none" />
                        {/* Title and logo positioned absolutely at bottom */}
                        <div className="absolute bottom-0 left-0 right-0 p-3 text-center z-10">
                          <h3 className="text-white text-base font-semibold mb-2 leading-tight" style={{ textShadow: '0 2px 4px rgba(0, 0, 0, 0.8)' }}>
                            {item.contentTitle}
                          </h3>
                          <div className="flex justify-center scale-75">
                            <LogoSelector
                              brandId={item.brandId}
                              brandName={item.brandName}
                              defaultLogo={resolvedLogo}
                              logoDisplayMode={resolvedDisplayMode}
                              canEdit={false}
                              onChange={() => {}}
                              skipBrandUpdate={true}
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
                {item.sourceUrl && <LinkPreview url={item.sourceUrl} />}
              </div>
            )}
            <CommonFooter item={item} />
          </>
        );
      case 'facebook':
        return (
          <>
            <p className="text-sm text-gray-800 whitespace-pre-line leading-relaxed">{item.content}</p>
            {renderMentions()}
            {renderHashtagRow()}
            {(item.sourceUrl || images.length) && (
              <div className="mt-3 space-y-3">
                {images.length > 0 && (
                  <div className="relative">
                    <img
                      src={images[0]}
                      alt="Facebook preview"
                      className="w-full h-48 object-cover rounded-lg border border-gray-200"
                    />
                    {/* Title and Logo Overlay */}
                    {item.contentTitle && item.brandId && (
                      <>
                        {/* Gradient overlay for text readability - dark at bottom fading up */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent rounded-lg pointer-events-none" />
                        {/* Title and logo positioned absolutely at bottom */}
                        <div className="absolute bottom-0 left-0 right-0 p-3 text-center z-10">
                          <h3 className="text-white text-base font-semibold mb-2 leading-tight" style={{ textShadow: '0 2px 4px rgba(0, 0, 0, 0.8)' }}>
                            {item.contentTitle}
                          </h3>
                          <div className="flex justify-center scale-75">
                            <LogoSelector
                              brandId={item.brandId}
                              brandName={item.brandName}
                              defaultLogo={resolvedLogo}
                              logoDisplayMode={resolvedDisplayMode}
                              canEdit={false}
                              onChange={() => {}}
                              skipBrandUpdate={true}
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
                <LinkPreview url={item.sourceUrl} />
              </div>
            )}
            <CommonFooter item={item} />
          </>
        );
      case 'tiktok':
        return (
          <>
            {/* Show video player if video_url exists (POLLINATE content) */}
            {item.videoUrl && (
              <div className="relative mb-3">
                <video
                  src={item.videoUrl}
                  controls
                  poster={item.videoThumbnailUrl || undefined}
                  className="w-full max-h-96 rounded-lg border border-gray-200 bg-black"
                  playsInline
                >
                  Your browser does not support the video tag.
                </video>
                {item.videoDurationSeconds && (
                  <div className="mt-1 text-xs text-gray-500">
                    Duration: {Math.round(item.videoDurationSeconds)}s
                  </div>
                )}
              </div>
            )}
            <p className="text-sm text-gray-800 whitespace-pre-line leading-relaxed">{item.content}</p>
            {item.mediaPrompt && !item.videoUrl && (
              <div className="mt-3 bg-black text-white rounded-lg p-3 text-sm">
                <div className="uppercase text-xs opacity-70">Video Concept</div>
                <div>{item.mediaPrompt}</div>
              </div>
            )}
            {renderMentions()}
            {renderHashtagRow()}
            <CommonFooter item={item} />
          </>
        );
      case 'youtube_shorts':
        return (
          <>
            {/* Show video player if video_url exists (POLLINATE content) */}
            {item.videoUrl && (
              <div className="relative mb-3">
                <video
                  src={item.videoUrl}
                  controls
                  poster={item.videoThumbnailUrl || undefined}
                  className="w-full max-h-96 rounded-lg border border-gray-200 bg-black"
                  playsInline
                >
                  Your browser does not support the video tag.
                </video>
                {item.videoDurationSeconds && (
                  <div className="mt-1 text-xs text-gray-500">
                    Duration: {Math.round(item.videoDurationSeconds)}s
                  </div>
                )}
              </div>
            )}
            <p className="text-sm text-gray-800 whitespace-pre-line leading-relaxed">{item.content}</p>
            {item.mediaPrompt && !item.videoUrl && (
              <div className="mt-3 border border-red-200 text-red-600 rounded-lg p-3 text-sm">
                <div className="uppercase text-xs font-semibold">Thumbnail Concept</div>
                <div>{item.mediaPrompt}</div>
              </div>
            )}
            {renderMentions()}
            {renderHashtagRow()}
            <CommonFooter item={item} />
          </>
        );
      case 'pollinate':
        return (
          <>
            {images.length > 0 && (
              <div className="relative mb-3">
                <video
                  src={images[0]}
                  controls
                  className="w-full max-h-96 rounded-lg border border-gray-200 bg-black"
                  playsInline
                >
                  Your browser does not support the video tag.
                </video>
              </div>
            )}
            <p className="text-sm text-gray-800 whitespace-pre-line leading-relaxed">{item.content}</p>
            {item.metadata?.duration && typeof item.metadata.duration === 'number' && (
              <div className="mt-2 text-xs text-gray-500">
                Duration: {item.metadata.duration}s
              </div>
            )}
            <CommonFooter item={item} />
          </>
        );
      default:
        return (
          <>
            {/* Show video player if video_url exists (POLLINATE content) */}
            {item.videoUrl ? (
              <div className="relative mb-3">
                <video
                  src={item.videoUrl}
                  controls
                  poster={item.videoThumbnailUrl || undefined}
                  className="w-full max-h-96 rounded-lg border border-gray-200 bg-black"
                  playsInline
                >
                  Your browser does not support the video tag.
                </video>
                {item.videoDurationSeconds && (
                  <div className="mt-1 text-xs text-gray-500">
                    Duration: {Math.round(item.videoDurationSeconds)}s
                  </div>
                )}
              </div>
            ) : images.length > 0 && (
              <div className="relative mb-3">
                <img
                  src={images[0]}
                  alt="Content preview"
                  className="w-full h-48 object-cover rounded-lg border border-gray-200"
                />
                {/* Title and Logo Overlay */}
                {item.contentTitle && item.brandId && (
                  <>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent rounded-lg pointer-events-none" />
                    <div className="absolute bottom-0 left-0 right-0 p-3 text-center z-10">
                      <h3 className="text-white text-base font-semibold mb-2 leading-tight" style={{ textShadow: '0 2px 4px rgba(0, 0, 0, 0.8)' }}>
                        {item.contentTitle}
                      </h3>
                      <div className="flex justify-center scale-75">
                        <LogoSelector
                          brandId={item.brandId}
                          brandName={item.brandName}
                          defaultLogo={resolvedLogo}
                          logoDisplayMode={resolvedDisplayMode}
                          canEdit={false}
                          onChange={() => {}}
                          skipBrandUpdate={true}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
            <p className="text-sm text-gray-800 whitespace-pre-line leading-relaxed">{item.content}</p>
            {renderMentions()}
            {renderHashtagRow()}
            {item.sourceUrl && (
              <div className="mt-3">
                <LinkPreview url={item.sourceUrl} />
              </div>
            )}
            <CommonFooter item={item} />
          </>
        );
    }
  };

  return (
    <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden flex flex-col">
      <div className="p-4 bg-white border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <span
              className="text-2xl mr-3"
              style={{ color: item.platform.color }}
            >
              {item.platform.icon}
            </span>
            <div>
              <h3 className="font-semibold text-gray-900">{item.platform.name}</h3>
              <p className="text-xs text-gray-500">Styled preview</p>
            </div>
          </div>
          {/* Smart Publish Button - Ayrshare Integration */}
          {/* Use curatorBrandId for publishing (falls back to brandId for backwards compatibility) */}
          {(item.curatorBrandId || item.brandId) && item.socialContentId && (
            <SmartPublishButton
              brandId={item.curatorBrandId || item.brandId!}
              contentId={item.socialContentId}
              targetPlatform={item.platform.id}
              onPublishSuccess={(result) => {
                console.log('Published successfully:', result);
              }}
              onPublishError={(error) => {
                console.error('Publish error:', error);
              }}
              compact={false}
            />
          )}
        </div>
      </div>
      <div className="p-4 bg-white flex-1">
        {renderContentByPlatform()}
      </div>

      {/* BLOOM Button Section - Only show for content with social_content_id */}
      {item.socialContentId && (
        <div className="p-4 bg-gradient-to-r from-blue-50 to-sky-50 border-t border-gray-200">
          {publishResult?.success ? (
          <div className="space-y-2">
            <div className="flex items-center text-green-600 text-sm font-medium">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Published to Bluesky!
            </div>
            {publishResult.post_url && (
              <a
                href={publishResult.post_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800 underline"
              >
                View on Bluesky
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            )}
          </div>
        ) : publishError ? (
          <div className="space-y-2">
            <div className="text-red-600 text-sm">{publishError}</div>
            <button
              onClick={handlePublishToBluesky}
              disabled={isPublishing}
              className="w-full px-4 py-2 bg-gradient-to-r from-blue-500 to-sky-500 hover:from-blue-600 hover:to-sky-600 text-white rounded-lg font-semibold text-sm transition-all flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="mr-2">🦋</span>
              Retry Publishing
            </button>
          </div>
        ) : (
          <button
            onClick={handlePublishToBluesky}
            disabled={isPublishing}
            className="w-full px-4 py-2 bg-gradient-to-r from-blue-500 to-sky-500 hover:from-blue-600 hover:to-sky-600 text-white rounded-lg font-semibold text-sm transition-all flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
          >
            {isPublishing ? (
              <>
                <svg className="animate-spin h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Publishing...
              </>
            ) : (
              <>
                <span className="mr-2">🦋</span>
                Publish to Bluesky
              </>
            )}
          </button>
        )}
        </div>
      )}
    </div>
  );
};

export default TendedPlatformPreview;
